/**
 * @fileoverview Preserve upload actions while their security admission is pending.
 * `kc-internal-frontend/src/api.js` and `components/DocumentChat/api.js` install it as a
 * response interceptor. When a domain endpoint answers 202 `file_scan_pending`, it polls
 * `kc-internal-backend/routes/file_admission_routes.py` and replays the original request
 * once approved. Only that explicit response contract permits replay; domain endpoints
 * recheck authorization and exact-byte approval. Cancellation, a 15-minute deadline, and a
 * 20-replay cap stop hidden polling. `kc-file-admission` window events let
 * `ToastProvider.js` show pending and held notices.
 */

const PENDING = new Set(['created', 'uploading', 'completing', 'scan_pending', 'inspecting', 'promoting']);
const APPROVED = new Set(['approved', 'delivering', 'delivered']);

function announce(state) {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('kc-file-admission', { detail: { state } }));
}

function failure(message, response, code = 'FILE_ADMISSION_HELD') {
  const error = new Error(message);
  error.code = code;
  error.response = { ...response, status: 423, data: { error: message, code: 'file_scan_held' } };
  return error;
}

function pause(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(Object.assign(new Error('Upload canceled'), { code: 'ERR_CANCELED' }));
    const onAbort = () => {
      clearTimeout(timer);
      reject(Object.assign(new Error('Upload canceled'), { code: 'ERR_CANCELED' }));
    };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export function admissionResponseHandler(api, { sleep = pause, now = Date.now } = {}) {
  return async response => {
    if (response.status !== 202 || response.data?.code !== 'file_scan_pending') return response;
    const admission = response.data.file_admission;
    const config = response.config;
    if (!config || !/^\/file-admissions\/[a-f0-9]{64}$/.test(admission?.status_path || '')) {
      throw failure('File security status is unavailable. Please retry.', response);
    }
    const deadline = config._fileAdmissionDeadline || now() + 15 * 60 * 1000;
    const replays = config._fileAdmissionReplays || 0;
    if (replays >= 20) throw failure('Too many pending file checks. Please retry later.', response);
    announce('pending');
    while (now() < deadline) {
      await sleep(3000, config.signal);
      const status = (await api.get(admission.status_path, { signal: config.signal })).data;
      if (status.id !== admission.id) throw failure('File security status does not match this upload.', response);
      if (APPROVED.has(status.state)) {
        announce('approved');
        return api({ ...config, _fileAdmissionDeadline: deadline, _fileAdmissionReplays: replays + 1 });
      }
      if (!PENDING.has(status.state)) {
        announce('held');
        throw failure('This file is held by security checks. Please contact your legal team.', response);
      }
    }
    throw failure('Security checks are still running. You can retry this upload later.', response, 'FILE_ADMISSION_PENDING');
  };
}

export function installFileAdmissionTransport(api) {
  return api.interceptors.response.use(admissionResponseHandler(api));
}
